import { StarIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { FiMessageCircle } from "react-icons/fi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth } from "../../contexts/Auth";
import { usePremium } from "../../contexts/Premium";
import RedwingLogo from "../../resources/images/logo-512.png";
import {
  getMySupportConversation,
  markSupportConversationRead,
  sendSupportMessage,
} from "../../services/supportApi";

// Same dark surfaces as the goal modals and comic notes.
const PANEL_BG = "#171C2D";
const SURFACE_BG = "#1A2138";
const READER_BG = "#2A3350";
const BORDER_COLOR = "#30384E";
const MUTED_COLOR = "#A8B2C8";

const GUEST_EMAIL_KEY = "@SupportEmail";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const readGuestEmail = () => {
  try {
    return window.localStorage.getItem(GUEST_EMAIL_KEY) || "";
  } catch (e) {
    return "";
  }
};

const rememberGuestEmail = (email) => {
  try {
    window.localStorage.setItem(GUEST_EMAIL_KEY, email);
  } catch (e) {}
};

const timeLabel = (createdAt) =>
  createdAt
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(createdAt))
    : "";

// Premium styling shared with PremiumBanner.
const PriorityBadge = () => (
  <Flex
    as="span"
    display="inline-flex"
    align="center"
    gap="1"
    px="2"
    py="0.5"
    bg="rgba(255, 170, 0, 0.08)"
    borderWidth="1px"
    borderColor="rgba(255, 170, 0, 0.52)"
    borderRadius="full"
    color="#FFAA00"
    fontSize="xs"
    fontWeight="semibold"
    whiteSpace="nowrap"
  >
    <StarIcon boxSize="10px" />
    Priority support
  </Flex>
);

const MessageBubble = ({ sender, body, createdAt }) => {
  const isReader = sender === "user";

  return (
    <Flex direction="column" align={isReader ? "flex-end" : "flex-start"}>
      <Box
        bg={isReader ? READER_BG : SURFACE_BG}
        borderWidth="1px"
        borderColor={BORDER_COLOR}
        px="3"
        py="2"
        borderRadius="lg"
        borderBottomRightRadius={isReader ? "sm" : "lg"}
        borderBottomLeftRadius={isReader ? "lg" : "sm"}
        maxW="85%"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        fontSize="sm"
      >
        {body}
      </Box>
      {createdAt && (
        <Text fontSize="xs" color="gray.500" mt="1">
          {isReader ? "You" : "Redwing"} · {timeLabel(createdAt)}
        </Text>
      )}
    </Flex>
  );
};

/**
 * Floating support chat (replaces Tidio). Signed-in readers see their thread
 * and our replies; guests leave an email and are answered there.
 */
const SupportChat = () => {
  const { authData } = useAuth();
  const { isPremium } = usePremium();
  const isSignedIn = !!authData?.token;
  const queryClient = useQueryClient();
  const scrollRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [email, setEmail] = useState(readGuestEmail);
  const [emailError, setEmailError] = useState("");
  // Guests can't fetch their thread, so keep what they sent this session.
  const [guestMessages, setGuestMessages] = useState([]);

  const conversationQuery = useQuery(
    ["supportConversation"],
    getMySupportConversation,
    {
      enabled: isSignedIn,
      refetchOnWindowFocus: true,
      refetchInterval: isOpen ? 20000 : 300000,
    }
  );
  const conversation = isSignedIn ? conversationQuery.data?.data : null;
  const hasUnreadReply = !!conversation?.unreadReply;

  const readMutation = useMutation(markSupportConversationRead, {
    onSuccess: () => {
      queryClient.setQueryData(["supportConversation"], (previous) =>
        previous?.data
          ? { ...previous, data: { ...previous.data, unreadReply: false } }
          : previous
      );
    },
  });

  useEffect(() => {
    if (isOpen && hasUnreadReply && !readMutation.isLoading) {
      readMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasUnreadReply]);

  const sendMutation = useMutation(sendSupportMessage, {
    onSuccess: (response, variables) => {
      setDraft("");
      if (isSignedIn) {
        queryClient.setQueryData(["supportConversation"], response);
      } else {
        rememberGuestEmail(variables.email);
        setGuestMessages((previous) => [
          ...previous,
          ...(response?.data?.messages || []).slice(-1),
        ]);
      }
    },
  });

  const messages = isSignedIn ? conversation?.messages || [] : guestMessages;
  const isClosed = conversation?.status === "closed";

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isOpen, messages.length]);

  const submit = (event) => {
    event?.preventDefault();
    const message = draft.trim();

    if (!message || sendMutation.isLoading) {
      return;
    }

    if (!isSignedIn && !EMAIL_PATTERN.test(email.trim())) {
      setEmailError("Enter a valid email so we can reply to you.");
      return;
    }

    setEmailError("");
    sendMutation.mutate({
      message,
      email: isSignedIn ? null : email.trim(),
      pageUrl: window.location.href,
    });
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      submit(event);
    }
  };

  const sendError =
    sendMutation.error?.response?.status === 429
      ? "You're sending messages too quickly. Please wait a minute."
      : sendMutation.error
      ? "Your message could not be sent. Please try again."
      : null;

  const greeting = isSignedIn
    ? `Hi ${authData.firstName || "there"} 👋 How can we help?`
    : "Hi 👋 How can we help?";

  return (
    <Box
      position="fixed"
      bottom={{ base: "4", md: "6" }}
      right={{ base: "4", md: "6" }}
      zIndex="overlay"
    >
      {isOpen && (
        <Flex
          role="dialog"
          aria-label="Redwing support chat"
          direction="column"
          bg={PANEL_BG}
          borderWidth="1px"
          borderColor={BORDER_COLOR}
          borderRadius="xl"
          boxShadow="dark-lg"
          position="absolute"
          bottom="16"
          right="0"
          w={{ base: "calc(100vw - 2rem)", sm: "360px" }}
          h="min(520px, calc(100vh - 8rem))"
          overflow="hidden"
        >
          <Flex
            align="center"
            px="4"
            py="3"
            borderBottomWidth="1px"
            borderColor={BORDER_COLOR}
          >
            <Flex
              align="center"
              justify="center"
              boxSize="10"
              flexShrink="0"
              mr="3"
              bg="black"
              borderRadius="full"
              borderWidth="1px"
              borderColor={BORDER_COLOR}
            >
              <Image src={RedwingLogo} alt="Redwing" boxSize="7" />
            </Flex>
            <Box>
              <Text as="h2" fontSize="lg" fontWeight="semibold">
                Redwing support
              </Text>
              {isSignedIn && isPremium ? (
                <PriorityBadge />
              ) : (
                <Text fontSize="sm" color={MUTED_COLOR}>
                  We usually reply within a day.
                </Text>
              )}
            </Box>
            <CloseButton
              ml="auto"
              aria-label="Close support chat"
              onClick={() => setIsOpen(false)}
            />
          </Flex>

          <Stack ref={scrollRef} flex="1" overflowY="auto" spacing="3" p="4">
            <MessageBubble sender="admin" body={greeting} />
            {messages.map((message) => (
              <MessageBubble key={message.id} {...message} />
            ))}
            {!isSignedIn && guestMessages.length > 0 && (
              <Text fontSize="xs" color="gray.500" textAlign="center">
                Thanks! We'll reply to {email} by email.
              </Text>
            )}
            {isClosed && (
              <Text fontSize="xs" color="gray.500" textAlign="center">
                This conversation is closed. Send a message to start a new one.
              </Text>
            )}
          </Stack>

          <Stack
            as="form"
            onSubmit={submit}
            spacing="3"
            p="4"
            borderTopWidth="1px"
            borderColor={BORDER_COLOR}
          >
            {!isSignedIn && (
              <FormControl id="support-email" isInvalid={!!emailError}>
                <FormLabel color="gray.400" fontSize="sm">
                  Your email
                </FormLabel>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder="So we can reply to you"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <FormErrorMessage>{emailError}</FormErrorMessage>
              </FormControl>
            )}
            <Textarea
              rows={3}
              resize="none"
              placeholder="Write a message"
              aria-label="Message"
              maxLength={5000}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
            />
            {sendError && (
              <Text fontSize="sm" color="red.300">
                {sendError}
              </Text>
            )}
            <Button
              type="submit"
              colorScheme="red"
              color="white"
              minH="44px"
              isLoading={sendMutation.isLoading}
              isDisabled={!draft.trim()}
            >
              Send message
            </Button>
          </Stack>
        </Flex>
      )}

      <Box position="relative">
        <Button
          aria-label={isOpen ? "Close support chat" : "Open support chat"}
          bg={PANEL_BG}
          color="white"
          borderWidth="1px"
          borderColor={BORDER_COLOR}
          _hover={{ bg: "#222B45" }}
          _active={{ bg: READER_BG }}
          borderRadius="full"
          w="14"
          h="14"
          p="0"
          boxShadow="lg"
          onClick={() => setIsOpen((open) => !open)}
        >
          <FiMessageCircle size="26" />
        </Button>
        {hasUnreadReply && !isOpen && (
          <Box
            data-testid="support-unread-dot"
            position="absolute"
            top="0"
            right="0"
            w="3.5"
            h="3.5"
            bg="green.400"
            borderRadius="full"
            borderWidth="2px"
            borderColor={PANEL_BG}
          />
        )}
      </Box>
    </Box>
  );
};

export default SupportChat;
